import { Component, ElementRef, Inject, OnDestroy, ViewChild } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { buildUserRow } from "src/app/components/du-admin/models/du-admin-user.model";
import { DuAdminService } from "src/app/components/du-admin/services/du-admin.service";
import { ApiService } from "src/app/services/api.service";
import { apiErrorMessage, browserTimeZone, utcToZoned, VISIT_TIME_ZONES, zonedToUtc } from "../visit-time";

export interface VisitFormData {
  start?: Date;
  visit?: any;
  /** Prefill when opening Create Meeting from a workflow task. */
  visitType?: "VIDEO" | "IN_PERSON";
  title?: string;
}

/** Someone the attendee field can suggest, from the portal's user directory (`get-user-list`). */
export interface VisitPerson {
  name: string;
  email: string;
}

const MAX_ATTENDEES = 25;
const MAX_SUGGESTIONS = 8;
/** An address as a mail client takes it: something@domain.tld, no spaces. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: "app-visit-form",
  templateUrl: "./visit-form.component.html",
  styleUrls: ["./visit-form.component.scss"],
  standalone: false,
})
export class VisitFormComponent implements OnDestroy {
  readonly timeZones = VISIT_TIME_ZONES;
  readonly editing: boolean;
  form: FormGroup;
  saving = false;
  errorMessage = "";
  resultMessage = "";

  /** The user directory the attendee field suggests from; null while it loads. */
  people: VisitPerson[] | null = null;
  peopleError = "";
  /** What is typed in the attendee field. */
  query = "";
  /** Directory matches for the query, leaving out people already added. */
  suggestions: VisitPerson[] = [];
  /** Why the typed entry was not added: not an address, already added, or the limit. */
  peopleMessage = "";
  /** The autocomplete writes the picked option into the field; the chips show it instead. */
  readonly noText = () => "";

  @ViewChild("peopleInput") peopleInput?: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) peopleTrigger?: MatAutocompleteTrigger;

  private subs: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private duAdmin: DuAdminService,
    private dialogRef: MatDialogRef<VisitFormComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: VisitFormData
  ) {
    const visit = data?.visit;
    this.editing = !!visit;
    const zone = visit?.timeZone || browserTimeZone();
    const start = visit ? new Date(visit.startUtc) : this.defaultStart(data?.start);
    const end = visit ? new Date(visit.endUtc) : new Date(start.getTime() + 30 * 60000);
    const startParts = utcToZoned(start, zone);
    const endParts = utcToZoned(end, zone);
    const prefillTitle = (data?.title || "").trim();
    const prefillType = data?.visitType === "VIDEO" || data?.visitType === "IN_PERSON" ? data.visitType : "IN_PERSON";

    this.form = this.fb.group(
      {
        title: [visit?.title || prefillTitle, [Validators.required, Validators.maxLength(200)]],
        visitType: [visit?.visitType || prefillType, Validators.required],
        date: [startParts.date, Validators.required],
        startTime: [startParts.time, Validators.required],
        endTime: [endParts.time, Validators.required],
        timeZone: [zone, Validators.required],
        location: [visit?.location || "", Validators.maxLength(500)],
        notes: [visit?.notes || "", Validators.maxLength(2000)],
        attendees: this.fb.array([], Validators.required),
      },
      { validators: (group: AbstractControl) => this.timeRangeValidator(group) }
    );

    // An edited visit keeps each attendee's type and NPI; new chips take the form's defaults.
    const attendees: any[] = visit?.attendees || [];
    attendees.forEach((a) => this.addAttendee(a));
    this.loadPeople();

    // "Please fix the highlighted fields." goes away once there is nothing left to fix.
    this.form.statusChanges.subscribe((status) => {
      if (status === "VALID" && this.errorMessage && !this.saving) this.errorMessage = "";
    });
  }

  get attendees(): FormArray {
    return this.form.get("attendees") as FormArray;
  }

  get attendeeGroups(): FormGroup[] {
    return this.attendees.controls as FormGroup[];
  }

  get isVideo(): boolean {
    return this.form.value.visitType === "VIDEO";
  }

  get canAddAttendee(): boolean {
    return this.attendees.length < MAX_ATTENDEES;
  }

  addAttendee(a: any = {}): void {
    if (!this.canAddAttendee) return;
    const group = this.fb.group({
      email: [a.email || "", [Validators.required, Validators.email, Validators.maxLength(320)]],
      displayName: [a.displayName || "", Validators.maxLength(200)],
      attendeeType: [a.attendeeType || "HCP", Validators.required],
      npi: [a.npi || "", Validators.pattern(/^\d{10}$/)],
    });
    group.get("attendeeType")!.valueChanges.subscribe((type) => {
      if (type !== "HCP") group.get("npi")!.setValue("");
    });
    this.attendees.push(group);
  }

  removeAttendee(index: number): void {
    this.attendees.removeAt(index);
    this.attendees.markAsTouched();
    this.refreshSuggestions();
  }

  // ── Attendee field: chips, typed addresses and directory suggestions ──────
  onQuery(value: string): void {
    this.query = value;
    this.peopleMessage = "";
    this.refreshSuggestions();
  }

  pickPerson(event: MatAutocompleteSelectedEvent): void {
    const person = event.option.value as VisitPerson;
    if (this.addPerson(person.email, person.name)) this.clearQuery();
  }

  /** Enter, comma, semicolon or Tab turn a typed address into a chip; Backspace on an empty field removes the last. */
  onPeopleKey(event: KeyboardEvent): void {
    // A highlighted suggestion is the autocomplete's to pick.
    if (this.peopleTrigger?.panelOpen && this.peopleTrigger.activeOption) return;
    const typed = this.query.trim();
    if (event.key === "Enter" || event.key === "," || event.key === ";" || (event.key === "Tab" && typed)) {
      // Enter here never submits the form.
      if (event.key !== "Tab" || typed) event.preventDefault();
      if (typed && this.addPerson(typed)) this.clearQuery();
      return;
    }
    if (event.key === "Backspace" && !this.query && this.attendees.length) {
      this.removeAttendee(this.attendees.length - 1);
    }
  }

  focusPeople(): void {
    this.peopleInput?.nativeElement.focus();
  }

  /** Two letters for a chip, from the name or else the address. */
  chipInitials(a: any): string {
    const parts = String(a?.displayName || a?.email || "")
      .split(/[\s@._-]+/)
      .filter((part) => !!part);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
  }

  /** Under the attendee field when there is no error: how to use it, or why it can't take more. */
  get peopleHint(): string {
    if (this.peopleError) return this.peopleError;
    return this.canAddAttendee ? "Search people by name or email, or type an email and press Enter." : `A visit can have up to ${MAX_ATTENDEES} attendees.`;
  }

  /** Under the attendee field: why a typed entry was not added, or that none is added once saving was tried. */
  get attendeesError(): string {
    if (this.peopleMessage) return this.peopleMessage;
    if (!this.attendees.invalid || !this.attendees.touched) return "";
    return this.attendees.errors?.["required"] ? "Add at least one attendee." : "Check the highlighted attendees.";
  }

  /** Adds a chip; false, with the reason in peopleMessage, when it can't. */
  private addPerson(email: string, name = ""): boolean {
    const address = email.trim().replace(/[,;]+$/, "");
    if (!EMAIL_RE.test(address)) {
      this.peopleMessage = `"${address}" isn't a valid email address.`;
      return false;
    }
    if (this.attendeeEmails().includes(address.toLowerCase())) {
      this.peopleMessage = `${name || address} is already added.`;
      return false;
    }
    if (!this.canAddAttendee) {
      this.peopleMessage = `A visit can have up to ${MAX_ATTENDEES} attendees.`;
      return false;
    }
    this.addAttendee({ email: address, displayName: name });
    this.attendees.markAsTouched();
    this.peopleMessage = "";
    return true;
  }

  private clearQuery(): void {
    this.query = "";
    if (this.peopleInput) this.peopleInput.nativeElement.value = "";
    this.suggestions = [];
  }

  private attendeeEmails(): string[] {
    return (this.attendees.value as any[]).map((a) => String(a?.email || "").trim().toLowerCase());
  }

  private refreshSuggestions(): void {
    const q = this.query.trim().toLowerCase();
    if (!q || !this.people) {
      this.suggestions = [];
      return;
    }
    const added = new Set(this.attendeeEmails());
    this.suggestions = this.people
      .filter((p) => !added.has(p.email.toLowerCase()) && (p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)))
      .slice(0, MAX_SUGGESTIONS);
  }

  /** `get-user-list` (the portal's whole user directory): active users with an address, by name. */
  private loadPeople(): void {
    this.subs.push(
      this.duAdmin.getUserList().subscribe({
        next: (res: any) => {
          const rows: any[] = Array.isArray(res) ? res : Array.isArray(res?.Table) ? res.Table : Array.isArray(res?.data) ? res.data : [];
          const seen = new Set<string>();
          this.people = rows
            .map((raw) => buildUserRow(raw))
            .filter((u) => u.status === "Active")
            .map((u) => ({ name: u.name || u.email, email: String(u.email || "").trim() }))
            .filter((p) => {
              const key = p.email.toLowerCase();
              if (!EMAIL_RE.test(p.email) || seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .sort((a, b) => a.name.localeCompare(b.name));
          this.refreshSuggestions();
        },
        error: () => {
          this.people = [];
          this.peopleError = "The user list could not be loaded. Type an email and press Enter.";
        },
      })
    );
  }

  fieldInvalid(name: string): boolean {
    const control = this.form.get(name);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  /** The message under a field once it has been touched. */
  fieldError(name: string): string {
    const errors = this.form.get(name)?.errors;
    if (!errors) return "";
    if (errors["required"]) return name === "title" ? "Enter a title for the visit." : "This is required.";
    if (errors["maxlength"]) return `Keep it under ${errors["maxlength"].requiredLength} characters.`;
    return "Check this field.";
  }

  /** The end-before-start rule, shown once either time has been touched. */
  get timeRangeError(): string {
    const touched = ["startTime", "endTime"].some((n) => this.form.get(n)?.touched || this.form.get(n)?.dirty);
    return touched ? this.form.errors?.["timeRange"] || "" : "";
  }

  get whenError(): string {
    if (this.fieldInvalid("date")) return "Pick a date.";
    if (this.fieldInvalid("startTime") || this.fieldInvalid("endTime")) return "Pick a start and an end time.";
    return this.timeRangeError;
  }

  /** "30 min", "1 hr 15 min" for the chosen times; empty until the range is valid. */
  get durationLabel(): string {
    const { startTime, endTime } = this.form.getRawValue();
    if (!startTime || !endTime || endTime <= startTime) return "";
    const toMinutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
    const minutes = toMinutes(endTime) - toMinutes(startTime);
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (!hours) return `${rest} min`;
    return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
  }

  get notesLength(): number {
    return (this.form.getRawValue().notes || "").length;
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  save(): void {
    this.errorMessage = "";
    // An address still in the attendee field is added rather than dropped; a repeat is just cleared.
    const typed = this.query.trim();
    if (typed) {
      if (this.attendeeEmails().includes(typed.toLowerCase()) || this.addPerson(typed)) this.clearQuery();
      else {
        this.errorMessage = "Please fix the highlighted fields.";
        return;
      }
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage = this.form.errors?.["timeRange"] || "Please fix the highlighted fields.";
      return;
    }

    const v = this.form.value;
    const payload = {
      visitKey: this.data?.visit?.visitKey || 0,
      title: v.title.trim(),
      visitType: v.visitType,
      startUtc: zonedToUtc(v.date, v.startTime, v.timeZone).toISOString(),
      endUtc: zonedToUtc(v.date, v.endTime, v.timeZone).toISOString(),
      timeZone: v.timeZone,
      location: v.visitType === "IN_PERSON" ? v.location?.trim() || null : null,
      notes: v.notes?.trim() || null,
      attendees: v.attendees.map((a: any) => ({
        email: a.email.trim(),
        displayName: a.displayName?.trim() || null,
        attendeeType: a.attendeeType,
        npi: a.attendeeType === "HCP" ? a.npi?.trim() || null : null,
      })),
    };

    this.saving = true;
    const request = this.editing ? this.apiService.updateVisit(payload) : this.apiService.createVisit(payload);
    request.subscribe({
      next: (res: any) => {
        this.saving = false;
        if (res?.invitationsFailed > 0) {
          this.resultMessage = `Visit saved. ${res.invitationsSent} email(s) sent, ${res.invitationsFailed} could not be delivered. ${res.invitationError || ""}`.trim();
          this.form.disable();
          return;
        }
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.saving = false;
        this.errorMessage = apiErrorMessage(err, "The visit could not be saved.");
      },
    });
  }

  done(): void {
    this.dialogRef.close(true);
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private defaultStart(clicked?: Date): Date {
    if (clicked && (clicked.getHours() !== 0 || clicked.getMinutes() !== 0)) return clicked;
    const base = clicked ? new Date(clicked) : new Date();
    if (clicked) base.setHours(9, 0, 0, 0);
    else {
      base.setMinutes(base.getMinutes() < 30 ? 30 : 60, 0, 0);
    }
    return base;
  }

  private timeRangeValidator(group: AbstractControl): ValidationErrors | null {
    const { date, startTime, endTime } = group.value || {};
    if (!date || !startTime || !endTime) return null;
    if (endTime <= startTime) return { timeRange: "End time must be after the start time." };
    return null;
  }
}
